/**
 * UI för Affärer/Pipeline-flödet: tratt, aktivitet per period, ekonomivy,
 * storleksväljare och bekräftelsen efter flytt till Vilande. Räknelogiken
 * ligger i pipelineFlode.js.
 */
import React, { useState } from 'react';
import {
  Box, Paper, Typography, Tooltip, ToggleButtonGroup, ToggleButton, Chip,
  CircularProgress, IconButton, Collapse, Snackbar, Button, TextField,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import {
  FLODESSKEDEN, STORLEKAR, PERIODER, KONVERTERING_MIN_UNDERLAG, VILANDE_ATERKONTAKT_MANADER,
} from './pipelineFlode';

const fmt = n => Math.round(n).toLocaleString('sv-SE');

// ── Storlek ─────────────────────────────────────────────────────────────────

/** Segmentväljare S / M / L / Vet ej. value: 'S'|'M'|'L'|null */
export function StorlekValjare({ value, onChange, size = 'small', disabled }) {
  return (
    <ToggleButtonGroup size={size} exclusive value={value || 'vetej'} disabled={disabled}
      onChange={(_, v) => { if (v) onChange(v === 'vetej' ? null : v); }}
      sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontWeight: 700, textTransform: 'none',
        '&.Mui-selected': { bgcolor: '#FF6B00', color: 'white', '&:hover': { bgcolor: '#e55a00' } } } }}>
      {STORLEKAR.map(s => (
        <ToggleButton key={s.key} value={s.key}>
          <Tooltip title={s.beskrivning}><span>{s.label}</span></Tooltip>
        </ToggleButton>
      ))}
      <ToggleButton value="vetej">Vet ej</ToggleButton>
    </ToggleButtonGroup>
  );
}

/** Liten bokstav på kanbankortet. Ritas bara när storleken är satt. */
export function StorlekBokstav({ storlek }) {
  const s = STORLEKAR.find(x => x.key === storlek);
  if (!s) return null;
  return (
    <Tooltip title={`Storlek ${s.label} – ${s.beskrivning}`}>
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 16, height: 16, borderRadius: '4px', border: '1px solid #bbb',
        fontSize: '0.6rem', fontWeight: 800, color: '#555', bgcolor: 'white', flexShrink: 0,
      }}>
        {s.label}
      </Box>
    </Tooltip>
  );
}

// ── Tratt ───────────────────────────────────────────────────────────────────

export function PipelineTratt({ data, stages, valtSkede, onValjSkede }) {
  const stage = key => stages.find(s => s.key === key);
  const total = FLODESSKEDEN.reduce((s, k) => s + data.antal[k], 0);
  const uppskattad = data.kalla === 'uppskattad';

  const vilande = stage('dormant');
  const vilandeValt = valtSkede === 'dormant';
  const extra = [
    { key: 'lost', label: 'Förlorad', n: data.forlorad },
  ];

  return (
    <Paper sx={{ mb: 2.5, p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight="bold"
          sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
          Flöde genom skedena
        </Typography>
        <Tooltip title={uppskattad
          ? `Uppskattad: antal i nästa skede och senare delat med antal i skedet och senare, på nuvarande skede. Räknas från skedeshistoriken när minst ${KONVERTERING_MIN_UNDERLAG} leads har hela sin resa loggad (nu ${data.underlag}).`
          : `Andel av leads som nått skedet och som också nått nästa, från skedeshistoriken (${data.underlag} leads).`}>
          <Typography variant="caption" sx={{ color: uppskattad ? '#E65100' : '#2E7D32', cursor: 'help' }}>
            {uppskattad ? 'Konvertering uppskattad' : 'Konvertering från historik'}
          </Typography>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Flödet, och Vilande sist med ett mellanrum efter Vunnen */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 1 }}>
          <Box sx={{ flexGrow: total || 1, flexBasis: 0, minWidth: 0 }}>
            {/* Stapeln */}
            <Box sx={{ display: 'flex', height: 28, borderRadius: 1, overflow: 'hidden', bgcolor: '#f0f0f0' }}>
              {FLODESSKEDEN.map(k => {
                const s = stage(k);
                const n = data.antal[k];
                const valt = valtSkede === k;
                return (
                  <Tooltip key={k} title={`${s.label}: ${n} – klicka för att filtrera tavlan`}>
                    <Box onClick={() => onValjSkede(valt ? null : k)}
                      sx={{
                        flexGrow: total ? n : 1, flexBasis: 0, minWidth: n ? 6 : 0,
                        bgcolor: s.color, cursor: 'pointer',
                        opacity: valtSkede && !valt ? 0.35 : 1,
                        borderRight: '2px solid white',
                        transition: 'opacity 0.15s',
                        '&:hover': { filter: 'brightness(1.1)' },
                      }} />
                  </Tooltip>
                );
              })}
            </Box>

            {/* Namn, antal och konvertering. Lika breda kolumner, så att texten
                går att läsa även när ett skede har få leads. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${FLODESSKEDEN.length}, 1fr)`, mt: 1 }}>
              {FLODESSKEDEN.map((k, i) => {
                const s = stage(k);
                const konv = i > 0 ? data.konvertering[i - 1] : undefined;
                return (
                  <Box key={k} onClick={() => onValjSkede(valtSkede === k ? null : k)}
                    sx={{ cursor: 'pointer', px: 0.5, borderLeft: i ? '1px solid #eee' : 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: s.color, flexShrink: 0 }} />
                      <Typography variant="caption" noWrap
                        sx={{ fontWeight: valtSkede === k ? 800 : 600, color: s.color }}>
                        {s.label}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{data.antal[k]}</Typography>
                    {konv !== undefined && (
                      <Tooltip title={`Konvertering ${stage(FLODESSKEDEN[i - 1]).label} → ${s.label}${uppskattad ? ' (uppskattad)' : ''}`}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                          ← {konv == null ? '–' : `${Math.round(konv * 100)} %`}{uppskattad ? '*' : ''}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Vilande: eget segment, ingen konvertering */}
          <Tooltip title={`Vilande: ${data.vilande}, varav ${data.vilandeAttAterkontakta} att återkontakta idag eller tidigare – klicka för att filtrera tavlan`}>
            <Box onClick={() => onValjSkede(vilandeValt ? null : 'dormant')}
              sx={{
                flexGrow: data.vilande || 1, flexBasis: 0, minWidth: 104, maxWidth: '25%',
                cursor: 'pointer', opacity: valtSkede && !vilandeValt ? 0.35 : 1, transition: 'opacity 0.15s',
              }}>
              <Box sx={{
                height: 28, borderRadius: 1, bgcolor: data.vilande ? vilande.color : '#f0f0f0',
                '&:hover': { filter: 'brightness(1.1)' },
              }} />
              <Box sx={{ mt: 1, px: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: vilande.color, flexShrink: 0 }} />
                  <Typography variant="caption" noWrap
                    sx={{ fontWeight: vilandeValt ? 800 : 600, color: vilande.color }}>
                    {vilande.label}
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{data.vilande}</Typography>
                <Typography variant="caption" noWrap
                  sx={{ display: 'block', fontSize: '0.68rem',
                    color: data.vilandeAttAterkontakta ? vilande.color : 'text.secondary',
                    fontWeight: data.vilandeAttAterkontakta ? 700 : 400 }}>
                  {data.vilandeAttAterkontakta} att återkontakta
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        </Box>

        {/* Förlorad, utanför stapeln */}
        <Box sx={{ display: 'flex', gap: 2, pl: 2, borderLeft: '1px solid #eee', flexShrink: 0 }}>
          {extra.map(e => (
            <Tooltip key={e.key} title={`${e.label}: ${e.n} – klicka för att filtrera tavlan`}>
              <Box onClick={() => onValjSkede(valtSkede === e.key ? null : e.key)}
                sx={{ cursor: 'pointer', textAlign: 'center', minWidth: 56 }}>
                <Typography variant="h6" fontWeight="bold"
                  sx={{ color: valtSkede === e.key ? '#555' : '#9e9e9e', lineHeight: 1.2, mt: 0.5 }}>
                  {e.n}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9e9e9e', fontWeight: valtSkede === e.key ? 800 : 400 }}>
                  {e.label}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
      {uppskattad && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.disabled', fontSize: '0.65rem' }}>
          * uppskattad
        </Typography>
      )}
    </Paper>
  );
}

// ── Aktivitet per period ────────────────────────────────────────────────────

export function AktivitetRad({ period, onPeriod, varden, motenLaddade, demosMatbara }) {
  const p = PERIODER.find(x => x.key === period);
  const matt = [
    { key: 'bokadeMoten', label: 'Bokade möten', laddar: !motenLaddade,
      hjalp: 'Möten registrerade på leads under perioden (räknas när de bokas). "Kunden kom inte" räknas inte.' },
    { key: 'genomfordaDemos', label: 'Genomförda demos',
      hjalp: 'Skedet Demo betyder att demon är bokad men inte hållen. En demo räknas som genomförd när leadet under perioden lämnat Demo för Offert, Förhandling eller Vunnen, enligt skedeshistoriken. Demo → Vilande räknas bara om demostatus är Genomförd.',
      ofullstandig: !demosMatbara },
    { key: 'skickadeOfferter', label: 'Skickade offerter',
      hjalp: 'Offerter registrerade på leads med datum i perioden.' },
  ];

  return (
    <Paper sx={{ mb: 2.5, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && onPeriod(v)}
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.25, py: 0.25, fontSize: '0.75rem',
          '&.Mui-selected': { bgcolor: '#1a1a1a', color: 'white', '&:hover': { bgcolor: '#333' } } } }}>
        {PERIODER.map(x => <ToggleButton key={x.key} value={x.key}>{x.label}</ToggleButton>)}
      </ToggleButtonGroup>
      {matt.map(m => (
        <Tooltip key={m.key} title={m.ofullstandig
          ? `${m.hjalp} Historiken täcker ännu inte hela jämförelseperioden.`
          : m.hjalp}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, cursor: 'help' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>
              {m.laddar ? <CircularProgress size={14} /> : varden[m.key].nu}
            </Typography>
            <Typography variant="body2" color="text.secondary">{m.label}</Typography>
            {!m.laddar && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {p.forra}: {m.ofullstandig ? '–' : varden[m.key].forra}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ))}
    </Paper>
  );
}

// ── Bekräftelse efter flytt till Vilande ───────────────────────────────────

const datumText = d => new Date(`${d}T12:00:00`).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * "Återkontakt satt till [datum]" med möjlighet att ändra datumet direkt.
 * bekraftelse: { leadId, titel, datum } | null. Föräldern sätter key per
 * bekräftelse, så att formuläret börjar om. onAndra(datum) sparar.
 */
export function AterkontaktBekraftelse({ bekraftelse, onAndra, onStang }) {
  const [andrar, setAndrar] = useState(false);
  const [datum, setDatum] = useState(bekraftelse?.datum || '');
  const [sparar, setSparar] = useState(false);

  async function spara() {
    if (!datum || sparar) return;
    setSparar(true);
    try {
      await onAndra(datum);
      onStang();
    } catch (e) {
      console.error('Kunde inte ändra återkontakt', e);
      alert('Kunde inte spara datumet: ' + (e.message || e));
    } finally {
      setSparar(false);
    }
  }

  const knapp = { color: '#FFB74D', fontWeight: 700, textTransform: 'none', minWidth: 0 };

  return (
    <Snackbar open={!!bekraftelse} autoHideDuration={andrar ? null : 10000}
      onClose={(_, reason) => { if (reason !== 'clickaway') onStang(); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
      <Paper elevation={6} role="status" sx={{
        bgcolor: '#323232', color: '#fff', px: 2, py: 1,
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', maxWidth: 560,
      }}>
        <EventIcon sx={{ fontSize: 18, color: '#BCAAA4' }} />
        {andrar ? (
          <>
            <Typography variant="body2" sx={{ color: '#fff' }}>Återkontakt</Typography>
            <TextField type="date" size="small" value={datum} autoFocus
              onChange={e => setDatum(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') spara(); }}
              inputProps={{ 'aria-label': 'Nytt datum för återkontakt' }}
              sx={{ bgcolor: '#fff', borderRadius: '4px', '& input': { py: 0.75 } }} />
            <Button size="small" onClick={spara} disabled={!datum || sparar} sx={knapp}>
              {sparar ? 'Sparar…' : 'Spara'}
            </Button>
            <Button size="small" onClick={() => { setAndrar(false); setDatum(bekraftelse?.datum || ''); }}
              sx={{ ...knapp, color: 'rgba(255,255,255,0.7)' }}>
              Avbryt
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Återkontakt satt till <b>{bekraftelse?.datum ? datumText(bekraftelse.datum) : '–'}</b>
              </Typography>
              {bekraftelse?.titel && (
                <Typography variant="caption" noWrap sx={{ display: 'block', color: 'rgba(255,255,255,0.6)' }}>
                  {bekraftelse.titel} · Vilande, {VILANDE_ATERKONTAKT_MANADER} månader om inget senare datum fanns
                </Typography>
              )}
            </Box>
            <Button size="small" onClick={() => setAndrar(true)} sx={knapp}>Ändra</Button>
            <IconButton size="small" aria-label="Stäng" onClick={onStang} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Paper>
    </Snackbar>
  );
}

// ── Ekonomi ─────────────────────────────────────────────────────────────────

const KALLA_ETIKETT = { offert: 'offert', 'värdefält': 'värdefält', saknas: 'saknar värde' };

function KallaText({ kallor }) {
  const delar = Object.entries(kallor).filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${KALLA_ETIKETT[k]}`);
  return <>{delar.join(', ') || '–'}</>;
}

export function EkonomiVy({ data, stages, navigate }) {
  const [oppen, setOppen] = useState({});
  const { rader, totalt } = data;
  const stage = key => stages.find(s => s.key === key) || stages[0];
  const th = { px: 2, py: 1, textAlign: 'left', fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary',
    textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid', borderColor: 'divider' };
  const td = { px: 2, py: 1.2 };

  return (
    <Paper sx={{ mb: 2.5, overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
        <TableChartIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
        <Typography variant="caption" fontWeight="bold" sx={{ color: 'white', letterSpacing: 1, textTransform: 'uppercase' }}>
          Ekonomi – produktöversikt (KSEK / år)
        </Typography>
      </Box>
      {rader.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>Inga vunna eller aktiva leads.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <Box component="thead">
              <Box component="tr" sx={{ bgcolor: '#f5f5f5' }}>
                {['Produkt', 'Befintliga kunder', 'KSEK (won)', 'Pipeline-leads', 'KSEK (pipeline)', 'Total', 'Källa'].map(h => (
                  <Box key={h} component="th" sx={th}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {rader.map(r => (
                <React.Fragment key={r.key}>
                  <Box component="tr" onClick={() => setOppen(o => ({ ...o, [r.key]: !o[r.key] }))}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#fafafa' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box component="td" sx={{ ...td, fontWeight: 700, fontStyle: r.utanProdukt ? 'italic' : 'normal' }}>
                      <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
                        {oppen[r.key] ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                      {r.namn}
                    </Box>
                    <Box component="td" sx={td}>
                      <Chip label={r.wonCount} size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700, height: 20 }} />
                    </Box>
                    <Box component="td" sx={{ ...td, fontWeight: 700, color: '#2E7D32' }}>
                      {r.wonKSEK > 0 ? `${fmt(r.wonKSEK)} KSEK` : '–'}
                    </Box>
                    <Box component="td" sx={td}>
                      <Chip label={r.activeCount} size="small" sx={{ bgcolor: '#FFF3E0', color: '#FF6B00', fontWeight: 700, height: 20 }} />
                    </Box>
                    <Box component="td" sx={{ ...td, fontWeight: 700, color: '#FF6B00' }}>
                      {r.activeKSEK > 0 ? `${fmt(r.activeKSEK)} KSEK` : '–'}
                    </Box>
                    <Box component="td" sx={{ ...td, fontWeight: 900 }}>{fmt(r.wonKSEK + r.activeKSEK)} KSEK</Box>
                    <Box component="td" sx={{ ...td, color: 'text.secondary', fontSize: '0.75rem' }}>
                      <KallaText kallor={r.kallor} />
                    </Box>
                  </Box>
                  <Box component="tr">
                    <Box component="td" colSpan={7} sx={{ p: 0, border: 0 }}>
                      <Collapse in={!!oppen[r.key]} unmountOnExit>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', bgcolor: '#fcfcfc', fontSize: '0.78rem' }}>
                          <Box component="tbody">
                            {r.leads.map(({ lead, ksek, kalla, offert }) => {
                              const s = stage(lead.status);
                              return (
                                <Box component="tr" key={lead.id}
                                  onClick={() => navigate(`/crm/leads/${lead.id}`)}
                                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f3f3f3' }, borderBottom: '1px solid #f0f0f0' }}>
                                  <Box component="td" sx={{ px: 2, py: 0.75, pl: 6 }}>{lead.title}</Box>
                                  <Box component="td" sx={{ px: 2, py: 0.75 }}>
                                    <Chip label={s.label} size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: s.bg, color: s.color }} />
                                  </Box>
                                  <Box component="td" sx={{ px: 2, py: 0.75, fontWeight: 700, textAlign: 'right' }}>
                                    {kalla ? `${fmt(ksek)} KSEK` : '–'}
                                  </Box>
                                  <Box component="td" sx={{ px: 2, py: 0.75, color: 'text.secondary' }}>
                                    {kalla === 'offert'
                                      ? `offert ${offert.datum}${offert.namn ? ` – ${offert.namn}` : ''}`
                                      : kalla === 'värdefält'
                                        ? `värdefält${lead.revenueUnit === 'manad' ? ' (per månad × 12)' : lead.revenueUnit === 'projekt' ? ' (per projekt)' : ''}`
                                        : 'saknar värde'}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>
                  </Box>
                </React.Fragment>
              ))}
              <Box component="tr" sx={{ bgcolor: '#1a1a1a' }}>
                <Box component="td" sx={{ ...td, fontWeight: 900, color: 'white' }}>Totalt</Box>
                <Box component="td" sx={{ ...td, color: 'white' }}>{totalt.wonCount}</Box>
                <Box component="td" sx={{ ...td, fontWeight: 900, color: '#66BB6A' }}>{fmt(totalt.wonKSEK)} KSEK</Box>
                <Box component="td" sx={{ ...td, color: 'white' }}>{totalt.activeCount}</Box>
                <Box component="td" sx={{ ...td, fontWeight: 900, color: '#FFB74D' }}>{fmt(totalt.activeKSEK)} KSEK</Box>
                <Box component="td" sx={{ ...td, fontWeight: 900, color: 'white' }}>{fmt(totalt.wonKSEK + totalt.activeKSEK)} KSEK</Box>
                <Box component="td" sx={td} />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2.5, py: 1.5 }}>
        Värdet per lead är beloppet på den senaste offerten. Saknas offert används leadets gamla värdefält:
        per månad räknas om × 12, per projekt räknas som det står. Leads utan produkt ligger på egen rad,
        så att totalen omfattar alla vunna och aktiva leads. Klicka på en rad för att se leadsen.
      </Typography>
    </Paper>
  );
}
